import React, { useEffect, useState, useCallback } from 'react';
import { bool, func, number, string } from 'prop-types';
import { Form as FinalForm, FormSpy } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { trackBeginCheckout } from '../../../util/gtmUtils';
import { numberAtLeast, required } from '../../../util/validators';
import { PURCHASE_PROCESS_NAME } from '../../../transactions/transaction';

import {
  Form,
  FieldTextInput,
  InlineTextButton,
  PrimaryButton,
  H6,
  FieldNumberInput,
} from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';
import ShippingPriceChecker from './ShippingPriceChecker';

import css from './ProductOrderForm.module.css';
import LISTING_STATUS from '../../../util/enums/LISTING_STATUS';

// Browsers can't render huge number of select options.
// (stock is shown inside select element)
// Note: input element could allow ordering bigger quantities
const MAX_QUANTITY_FOR_DROPDOWN = 100;

const handleFetchLineItems = ({
  quantity,
  listingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
}) => {
  // Fetch line items logic
  const stockReservationQuantity = Number.parseInt(quantity, 10);

  const isBrowser = typeof window !== 'undefined';

  if (isBrowser && stockReservationQuantity && !fetchLineItemsInProgress) {
    onFetchTransactionLineItems({
      orderData: {
        stockReservationQuantity: stockReservationQuantity,
      },
      listingId,
      isOwnListing,
    });
  }
};

const renderForm = formRenderProps => {
  const [mounted, setMounted] = useState(false);
  const {
    // FormRenderProps from final-form
    handleSubmit,
    form: formApi,

    // Custom props passed to the form component
    intl,
    formId,
    currentStock,
    allowOrdersOfMultipleItems,
    listingId,
    listing,
    isOwnListing,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    price,
    payoutDetailsWarning,
    marketplaceName,
    values,
  } = formRenderProps;

  // Note: don't add custom logic before useEffect
  useEffect(() => {
    setMounted(true);

    // Side-effect: fetch line-items after mounting if possible
    const { quantity } = values;
    const defaultQuantity = quantity || 1;

    handleFetchLineItems({
      quantity: defaultQuantity,
      listingId,
      isOwnListing,
      fetchLineItemsInProgress,
      onFetchTransactionLineItems,
    });
  }, []);

  // If form values change, update line-items for the order breakdown
  const handleOnChange = formValues => {
    const { quantity } = formValues.values;
    const defaultQuantity = quantity || 1;

    if (mounted) {
      handleFetchLineItems({
        quantity: defaultQuantity,
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
      });
    }
  };

  // In case quantity and deliveryMethod are missing focus on that select-input.
  // Otherwise continue with the default handleSubmit function.
  const handleFormSubmit = e => {
    if (listing && lineItems) {
      trackBeginCheckout({ listing, lineItems });
    }

    const { quantity } = values || {};
    if (!quantity || quantity < 1) {
      e.preventDefault();
      // Blur event will show validator message
      formApi.blur('quantity');
      formApi.focus('quantity');
    } else {
      handleSubmit(e);
    }
  };

  const breakdownData = {};
  const showBreakdown =
    !isOwnListing &&
    breakdownData &&
    lineItems &&
    !fetchLineItemsInProgress &&
    !fetchLineItemsError;

  const showContactUser = typeof onContactUser === 'function';

  const onClickContactUser = e => {
    e.preventDefault();
    onContactUser();
  };

  const contactSellerLink = (
    <InlineTextButton onClick={onClickContactUser}>
      <FormattedMessage id="ProductOrderForm.finePrintNoStockLinkText" />
    </InlineTextButton>
  );
  const quantityRequiredMsg = intl.formatMessage({
    id: 'ProductOrderForm.quantityRequired',
  });

  // Listing is out of stock if currentStock is zero.
  // Undefined/null stock means that stock has never been set.
  const hasNoStockLeft = currentStock != null && currentStock === 0;
  const hasStock = currentStock && currentStock > 0;
  const isReserved =
    listing.attributes?.publicData?.listingStatus === LISTING_STATUS.RESERVED;
  const hasOneItemLeft = currentStock === 1;

  const isDrop = listing?.attributes?.metadata?.isInDrop;

  const submitInProgress = fetchLineItemsInProgress;
  const submitDisabled = !hasStock || isDrop || isReserved;

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormSpy subscription={{ values: true }} onChange={handleOnChange} />

      {hasNoStockLeft ||
      !allowOrdersOfMultipleItems ||
      hasOneItemLeft ? null : (
        <FieldNumberInput
          id={`${formId}.quantity`}
          className={css.quantityField}
          name="quantity"
          disabled={!hasStock}
          label="Quantity"
          minValue={1}
          maxValue={currentStock}
          validate={numberAtLeast(quantityRequiredMsg, 1)}
        />
      )}

      {showBreakdown ? (
        <div className={css.breakdownWrapper}>
          <div as="h3" className={css.bookingBreakdownTitle}>
            <FormattedMessage id="ProductOrderForm.breakdownTitle" />
          </div>
          <hr className={css.totalDivider} />
          <EstimatedCustomerBreakdownMaybe
            breakdownData={breakdownData}
            lineItems={lineItems}
            currency={price.currency}
            marketplaceName={marketplaceName}
            processName={PURCHASE_PROCESS_NAME}
          />
        </div>
      ) : null}
      {!isOwnListing && (
        <>
          <div className={css.submitContainer}>
            <div className={css.submitButton}>
              <PrimaryButton
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                {isDrop ? (
                  <FormattedMessage id="ProductOrderForm.ctaButtonInDrop" />
                ) : isReserved ? (
                  <FormattedMessage id="ProductOrderForm.ctaButtonReserved" />
                ) : hasStock ? (
                  <FormattedMessage id="ProductOrderForm.ctaButton" />
                ) : (
                  <FormattedMessage id="ProductOrderForm.ctaButtonNoStock" />
                )}
              </PrimaryButton>
            </div>
          </div>
          <p className={css.finePrint}>
            {payoutDetailsWarning ? (
              payoutDetailsWarning
            ) : hasStock && isOwnListing ? (
              <FormattedMessage id="ProductOrderForm.ownListing" />
            ) : hasStock ? (
              <a
                href={`mailto:contact@usedicons.com?subject=${encodeURIComponent(
                  `Inquiry about: ${listing.attributes.title}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'underline', color: 'inherit' }}
              >
                Contact us for more information
              </a>
            ) : showContactUser ? (
              <FormattedMessage
                id="ProductOrderForm.finePrintNoStock"
                values={{ contactSellerLink }}
              />
            ) : null}
          </p>
          <ShippingPriceChecker listing={listing} />
        </>
      )}
    </Form>
  );
};

const ProductOrderForm = props => {
  const intl = useIntl();
  const { currentStock, allowOrdersOfMultipleItems } = props;

  const hasOneItemLeft = currentStock === 1;
  const hasOneItemMode = !allowOrdersOfMultipleItems && currentStock > 0;
  const initialValues = { quantity: 1 };

  return (
    <FinalForm
      initialValues={initialValues}
      {...props}
      intl={intl}
      render={renderForm}
    />
  );
};

ProductOrderForm.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  currentStock: null,
  listingId: null,
  isOwnListing: false,
  lineItems: null,
  fetchLineItemsError: null,
};

ProductOrderForm.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,

  // form
  formId: string.isRequired,
  onSubmit: func.isRequired,

  // listing
  listingId: propTypes.uuid,
  price: propTypes.money,
  currentStock: number,
  isOwnListing: bool,

  // line items
  lineItems: propTypes.lineItems,
  onFetchTransactionLineItems: func.isRequired,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // other
  onContactUser: func,
};

export default ProductOrderForm;
